import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js/min';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';

export interface IPhoneCountry {
  code: CountryCode;
  name: string;
  dialCode: string;
  flagClass: string;
}

function _buildCountryList(): IPhoneCountry[] {
  const names = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
  return getCountries()
    .map((code) => ({
      code,
      name: names.of(code) ?? code,
      dialCode: `+${getCountryCallingCode(code)}`,
      flagClass: `fi fi-${code.toLowerCase()}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** Lista de países (calculada uma vez no carregamento do módulo). */
const COUNTRIES: IPhoneCountry[] = _buildCountryList();

@Component({
  selector: 'lib-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, InputTextModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // ViewEncapsulation.None → flag-icons CSS (global) fica disponível no template
  encapsulation: ViewEncapsulation.None,
})
export class PhoneInputComponent implements OnInit, OnChanges {
  /**
   * Número completo (DDI + local, apenas dígitos).
   * Exemplo: '5511999887766' → Brasil +55 (11) 99988-7766.
   * Ao setar, o componente detecta o país automaticamente.
   */
  @Input() value = '';

  /** Código do país padrão ao inicializar. @default 'BR' */
  @Input() defaultCountry: CountryCode = 'BR';

  /** Placeholder do input de número. Se omitido, exibe número de exemplo do país. */
  @Input() placeholder = '';

  /** Desabilita o componente. */
  @Input() disabled = false;

  /**
   * Emite o número completo (DDI + local, apenas dígitos).
   * Exemplo: '5511999887766'.
   * Emite string vazia quando o campo está em branco.
   */
  @Output() valueChange = new EventEmitter<string>();

  readonly countries = COUNTRIES;

  /**
   * O bottom sheet fica em z-index ~1050 e o overlay padrão do PrimeNG em 1000.
   * baseZIndex 2000 vira 3000 (2000 + overlay 1000) e a lista abre na frente do sheet.
   */
  readonly overlayOptions = { baseZIndex: 2000 };

  selectedCountry: IPhoneCountry = COUNTRIES.find((c) => c.code === 'BR')!;
  displayValue = '';

  /** Último valor emitido. O form devolve esse mesmo valor e não pode ser reformatado. */
  private _ultimoEmitido = '';

  private _cdr = inject(ChangeDetectorRef);

  get computedPlaceholder(): string {
    if (this.placeholder) return this.placeholder;
    try {
      const EXAMPLES: Partial<Record<CountryCode, string>> = {
        BR: '31912345678',
        US: '2015551234',
        AR: '1112345678',
        PT: '912345678',
        MX: '5512345678',
        NZ: '210123456',
        AU: '412345678',
        CA: '412345678',
      };
      const digits = EXAMPLES[this.selectedCountry.code] ?? '9999999999';
      return new AsYouType(this.selectedCountry.code).input(digits);
    } catch {
      return '';
    }
  }

  ngOnInit(): void {
    this.selectedCountry =
      COUNTRIES.find((c) => c.code === this.defaultCountry) ??
      COUNTRIES.find((c) => c.code === 'BR')!;

    if (this.value) {
      this._parseFullNumber(this.value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultCountry'] && !changes['defaultCountry'].firstChange) {
      this.selectedCountry =
        COUNTRIES.find((c) => c.code === this.defaultCountry) ??
        this.selectedCountry;
    }
    if (changes['value'] && !changes['value'].firstChange) {
      const recebido = this.value ?? '';
      if (recebido === this._ultimoEmitido) return;

      this._ultimoEmitido = recebido;
      if (recebido) {
        this._parseFullNumber(recebido);
      } else {
        this.displayValue = '';
      }
      this._cdr.markForCheck();
    }
  }

  onCountryChange(country: IPhoneCountry): void {
    this.selectedCountry = country;
    const digits = this._garantirNoveBr(
      this._limitarDigitos(this.displayValue.replace(/\D/g, ''))
    );
    this.displayValue = digits ? new AsYouType(country.code).input(digits) : '';
    this._emit();
  }

  onPhoneInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const inputType = (event as InputEvent).inputType;
    const cursor = el.selectionStart ?? el.value.length;
    let digits = el.value.replace(/\D/g, '');
    const anteriores = this.displayValue.replace(/\D/g, '');
    let digitosAntes = el.value.slice(0, cursor).replace(/\D/g, '').length;

    // Backspace em "(" ou ")" não tira dígito; a máscara recolocava o (31).
    const apagouSoMascara =
      (inputType === 'deleteContentBackward' ||
        inputType === 'deleteContentForward') &&
      digits === anteriores &&
      digits.length > 0;

    if (apagouSoMascara) {
      const indice =
        inputType === 'deleteContentBackward'
          ? Math.max(0, digitosAntes - 1)
          : Math.min(digitosAntes, digits.length - 1);
      digits = digits.slice(0, indice) + digits.slice(indice + 1);
      digitosAntes = indice;
    }

    digits = this._limitarDigitos(digits);
    if (digitosAntes > digits.length) digitosAntes = digits.length;

    const formatted = digits
      ? new AsYouType(this.selectedCountry.code).input(digits)
      : '';
    const posicao = this._cursorAposDigitos(formatted, digitosAntes);
    this.displayValue = formatted;
    el.value = formatted;
    el.setSelectionRange(posicao, posicao);
    queueMicrotask(() => {
      if (document.activeElement === el) {
        el.setSelectionRange(posicao, posicao);
      }
    });
    this._emit();
  }

  /** Posição logo depois de `quantidade` dígitos no texto mascarado. */
  private _cursorAposDigitos(formatted: string, quantidade: number): number {
    if (quantidade <= 0) return 0;
    let vistos = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        vistos++;
        if (vistos === quantidade) return i + 1;
      }
    }
    return formatted.length;
  }

  onPhoneBlur(): void {
    const digits = this._garantirNoveBr(this.displayValue.replace(/\D/g, ''));
    const formatted = digits
      ? new AsYouType(this.selectedCountry.code).input(digits)
      : '';
    if (formatted === this.displayValue) return;
    this.displayValue = formatted;
    this._emit();
    this._cdr.markForCheck();
  }

  /** Descarta dígitos além do tamanho máximo do país. */
  private _limitarDigitos(digits: string): string {
    let atual = digits;
    while (
      atual.length > 0 &&
      validatePhoneNumberLength(atual, this.selectedCountry.code) === 'TOO_LONG'
    ) {
      atual = atual.slice(0, -1);
    }
    return atual;
  }

  /**
   * Celular BR antigo (DDD + 8 dígitos começando em 6–9) recebe o 9.
   * Não roda durante a digitação: 31997824221 já tem 11 dígitos e o 9.
   * Fixo (assinante 2–5) permanece com 10 dígitos.
   */
  private _garantirNoveBr(digits: string): string {
    if (this.selectedCountry.code !== 'BR' || digits.length !== 10) return digits;
    if (digits.charAt(2) < '6') return digits;
    return `${digits.slice(0, 2)}9${digits.slice(2)}`;
  }

  private _parseFullNumber(raw: string): void {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return;

    // Todo cadastro guarda o DDI (Brasil começa com 55). O libphonenumber
    // identifica o país; sem isso um 64 de 11 dígitos vira DDD brasileiro.
    try {
      const phone = parsePhoneNumber(`+${digits}`);
      if (phone?.country && phone.isValid()) {
        const country = COUNTRIES.find((c) => c.code === phone.country);
        if (country) {
          this.selectedCountry = country;
          const national = this._garantirNoveBr(String(phone.nationalNumber));
          this.displayValue = new AsYouType(phone.country).input(national);
          return;
        }
      }
    } catch {
      // número inválido: segue como local do país atual
    }

    // Número curto (≤11 dígitos) ou falha no parse: interpreta como número
    // local do país selecionado (cobre dados antigos sem DDI).
    const local = this._garantirNoveBr(this._limitarDigitos(digits));
    this.displayValue = new AsYouType(this.selectedCountry.code).input(local);
  }

  private _emit(): void {
    const localDigits = this.displayValue.replace(/\D/g, '');
    const dialDigits = this.selectedCountry.dialCode.replace('+', '');
    const completo = localDigits ? `${dialDigits}${localDigits}` : '';
    if (completo === this._ultimoEmitido) return;

    this._ultimoEmitido = completo;
    this.valueChange.emit(completo);
  }
}
